import Link from 'next/link';

import React, { MouseEvent, ReactElement, useCallback } from 'react';

import { IMG_URL } from '@common/const';
import { EventListDataType } from '@type/web/event';

import a11yHidden from '@lib/styles/a11yHidden';

import useMakeDateString from '@hook/useMakeDateString';
import useMakeDdayString from '@hook/useMakeDdayString';

import Icon from '@components/common/Icon';
import SkeletonEventList from '@components/common/Skeleton/Event/SkeletonEventList';
import { SkeletonImage } from '@components/common/Skeleton/SkeletonImage';

import {
  endColor,
  eventDetailStyle,
  eventLinkImageStyle,
  eventListStyle,
  eventListItemStyle,
  evnetbuttonStyle,
  orangeColor,
} from './EventList.style';

interface EventListProps {
  eventList?: Array<EventListDataType>;
  onShowPopUp: () => void;
  setShareData: (newShareData: any) => void;
}
interface EventLiProps {
  eventItem: EventListDataType;
  onShowPopUp: () => void;
  setShareData: (newShareData: any) => void;
}

interface EventDetailPropsType {
  exposeStartDate: string;
  exposeEndDate: string;
  remainDay: number;
}
interface EventLinkImagePropsType {
  eventTitle: string;
  bannerImgUrl: string;
  onClickShare: (e: MouseEvent<HTMLButtonElement>) => void;
}

export default function EventList({
  eventList,
  onShowPopUp,
  setShareData,
}: EventListProps): ReactElement {
  if (!eventList) return <SkeletonEventList />;

  return (
    <ul css={eventListStyle}>
      {eventList.map((eventItem: EventListDataType) => (
        <EventLi
          key={eventItem.eventSeq}
          eventItem={eventItem}
          onShowPopUp={onShowPopUp}
          setShareData={setShareData}
        />
      ))}
    </ul>
  );
}

const EventLi = ({
  eventItem,
  onShowPopUp,
  setShareData,
}: EventLiProps): ReactElement => {
  const {
    eventSeq,
    eventTitle,
    exposeStartDate,
    exposeEndDate,
    bannerImgUrl,
    remainDay,
  } = eventItem;

  const eventUrl = `/event/detail/${eventSeq}`;

  const onClickShare = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setShareData({
        bannerUrl: `${process.env.NEXT_PUBLIC_IMG_URL}${bannerImgUrl}`,
        bannerTitle: eventTitle,
        shareUrl: `/event/detail/${eventSeq}`,
        description: eventTitle,
        title: `[중고나라] ${eventTitle}`,
        text: eventTitle,
      });
      onShowPopUp();
    },
    [eventItem],
  );

  return (
    <li css={eventListItemStyle}>
      <Link href={eventUrl}>
        <a id={`event_${eventSeq}`}>
          <EventLinkImage
            eventTitle={eventTitle}
            bannerImgUrl={bannerImgUrl}
            onClickShare={onClickShare}
          />
        </a>
      </Link>
      <EventDetail
        exposeStartDate={exposeStartDate}
        exposeEndDate={exposeEndDate}
        remainDay={remainDay}
      />
    </li>
  );
};

const EventDetail = ({
  exposeStartDate,
  exposeEndDate,
  remainDay,
}: EventDetailPropsType): ReactElement => {
  const startDateString = useMakeDateString(exposeStartDate);
  const endDateString = useMakeDateString(exposeEndDate);
  const dDay = useMakeDdayString(remainDay);

  return (
    <div css={eventDetailStyle}>
      <p>
        <time dateTime={exposeStartDate}>{startDateString}</time>
        {' ~ '}
        <time dateTime={exposeEndDate}>{endDateString}</time>
      </p>
      <p css={!/종료/.test(dDay) ? orangeColor : endColor}>{dDay}</p>
    </div>
  );
};

const EventLinkImage = ({
  eventTitle,
  bannerImgUrl,
  onClickShare,
}: EventLinkImagePropsType): ReactElement => {
  return (
    <div css={eventLinkImageStyle}>
      <SkeletonImage src={`${IMG_URL}${bannerImgUrl}`} alt={eventTitle} />
      <button
        type="button"
        aria-label="share"
        css={evnetbuttonStyle}
        onClick={onClickShare}
      >
        <Icon icon="shareIOS" stroke="white" />
      </button>
    </div>
  );
};
